import ApiService from "@/api/ApiService";
import { useGlobalStore, usePersistantStore } from "@/stores/global";
import { Box, CircularProgress, IconButton, List, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Fragment } from "react/jsx-runtime";
import { PossibleCalendarTypes } from "./MainCalendar";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import { AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import FreeContainer from "./FreeContainer";
import {
  ArrowBackIos,
  ArrowBackIosNew,
  ArrowForwardIos,
  ArrowRight,
  AssignmentTurnedIn,
  BeachAccess,
  Bolt,
  Delete,
  ListAlt,
  Redo,
  Search,
} from "@mui/icons-material";
import CollisionContainer from "./CollisionContainer";
import TransferContainer from "./TransferContainer";
import { useNavigate } from "react-router-dom";

interface OpenRequest {
  fromdate: string;
  todate: string;
  pname: string;
  pnum: string;
  resource_tag_id: number;
  open: number;
  roid: number;
  comment: string;
  name: string;
  TYPE: "RESOURCE" | "CREW" | "HUMAN";
}
interface OpenRejection {
  date: string;
  rejection_id: number;
  comment: string;
  created_by: string;
  project_name: string;
  project_number: string;
  tag_id: number;
  tag_name: string;
  resource_name: string;
  resource_number: string;
  type: "RESOURCE" | "HUMAN";
}

const expandedEntryStyles = {
  "& .open-entry-text": {
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  "& .open-entry-comment": {
    display: "block",
    WebkitLineClamp: "unset",
  },
};

export default function OpenRequests({
  calendar,
  type,
  currentDate,
  pdfref,
}: {
  type: "LONG" | "SHORT";
  pdfref: any;
  currentDate?: string;
  calendar?: any;
}) {
  const navigate = useNavigate();
  const setDate = useGlobalStore((state) => state.setDate);
  const setKgpReference = usePersistantStore((state) => state.setKgpReference);
  const setType = usePersistantStore((state) => state.setType);
  const setReference = usePersistantStore((state) => state.setReference);

  const setCalendar = (newType, reference, date) => {
    if (newType === "KGP") {
      setDate(date);
      setKgpReference(reference);
      if (type !== "SHORT") {
        navigate("/kgp");
      }
    } else {
      setType(newType);
      setReference(reference);
      setDate(date);
      if (type !== "LONG") {
        navigate("/calendar");
      } else {
        calendar.current?.setCalendar(date.subtract(1, "day"), date.add(8, "days"));
      }
    }
  };

  const sideBarHidden = usePersistantStore((store) => store.getSettings().sideBarHidden);
  const setSideBarHidden = usePersistantStore((store) => (sideBarHidden: boolean) => store.setSettings({ sideBarHidden }));

  const region = usePersistantStore((store) => store.region);
  const dispoCategory = usePersistantStore((store) => store.dispoCategory);
  const setDispoCategory = usePersistantStore((store) => store.setDispoCategory);

  const [xtype, setXtype] = useState<"OPEN" | "FREE" | "COLLISIONS" | "TRANSFERS">("OPEN");
  const [week, setWeek] = useState(dayjs().startOf("isoWeek"));

  const queryClient = useQueryClient();

  const query = useQuery<(OpenRequest | OpenRejection)[], AxiosResponse>({
    queryKey: ["getOpenRequests", dispoCategory],
    enabled: !!dispoCategory,
    queryFn: ApiService.getOpenRequests(0, dispoCategory, "LONG"),
  });

  const resetRejectionMutation = useMutation<any, AxiosResponse, { id: number }>({
    mutationFn: ApiService.resetRejection(),
    onSettled: (answer) => {
      queryClient.invalidateQueries({ queryKey: ["getOpenRequests"] });
    },
  });

  const freeQuery = useQuery<any[], AxiosResponse>({
    queryKey: ["getOpenRequests", "free", "" + region, dispoCategory, week.format("YYYY-MM-DD")],
    enabled: !!dispoCategory && type === "LONG" && !!region,
    queryFn: ApiService.getFreeRequests(region, dispoCategory, week.format("YYYY-MM-DD")),
  });

  const transferQuery = useQuery<any[], AxiosResponse>({
    queryKey: ["getTransfers", "" + (region || 0), currentDate],
    enabled: !!currentDate && type === "SHORT",
    queryFn: ApiService.getDailyTransfers(region || 0, currentDate),
  });

  const collisionQuery = useQuery<any[], AxiosResponse>({
    queryKey: ["getOpenRequests", "collisions", "" + region, dispoCategory],
    enabled: !!dispoCategory && type === "LONG" && !!region,
    queryFn: ApiService.getCollisions(region, dispoCategory),
  });

  const [showThrash, setShowThrash] = useState(false);

  const dropPart = useCallback((e) => {
    const type = e.dataTransfer.types[0];
    setShowThrash(false);
    if (type !== "application/resource-booking") {
      return false;
    }

    const parts = e.dataTransfer.getData(e.dataTransfer.types[0]).split("#");
    const resourceBookingId = parseInt(parts[0]);
    const originDate = parts[1];
    const resourceId = parseInt(parts[2] ?? "0");
    const resourceType = parts[3] ?? "NONE";

    pdfref.current?.openModal({
      resourceBookingId,
      originDate,
      resourceId,
      resourceType,
      startDate: originDate,
      jobId: 0,
    });
  }, []);

  return sideBarHidden ? (
    <Box
      onClick={() => setSideBarHidden(false)}
      sx={{
        position: "absolute",
        bottom: "0",
        right: "0",
        borderTop: "1px solid #aaa",
        backgroundColor: "#fff",
        borderLeft: "1px solid #aaa",
        "&:hover": { backgroundColor: "#eee" },
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "5px",
      }}
    >
      <ArrowBackIosNew />
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px -2px 4px #0000004a",
        maxWidth: "250px",
        borderLeft: "1px solid #888",
        position: "relative",
      }}
      onDrop={(e) => dropPart(e)}
      onDragLeave={(e) => {
        e.preventDefault();
        setShowThrash(false);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setShowThrash(e.dataTransfer.types[0] === "application/resource-booking");
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setShowThrash(e.dataTransfer.types[0] === "application/resource-booking");
      }}
    >
      {xtype !== "TRANSFERS" ? (
        <Box sx={{ minWidth: "160px", borderBottom: "1px solid #888" }}>
          <ManyToOneSelect
            sx={{
              m: 0,
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiAutocomplete-clearIndicator": { display: "none !important" },
            }}
            remoteFilter={{ region: ["region", region] }}
            entity={"dispoCategory"}
            size="small"
            currentId={dispoCategory}
            disabled={false}
            setId={(id) => !id || setDispoCategory(id)}
            showWithoutChoice
            autoSelect
          />
        </Box>
      ) : null}
      {type === "LONG" ? (
        <Box
          sx={{
            borderBottom: "1px solid #aaa",
            cursor: "pointer",
            display: "flex",
          }}
        >
          <Box
            className={xtype === "OPEN" ? "act" : ""}
            sx={{
              "&:last-child": { borderRight: "none" },
              borderRight: "1px solid #aaa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              background: "#eee",
              color: "#555",
              "&:hover": {
                background: "#ddd",
                color: "#666",
              },
              "&.act": {
                background: "#fff",
                color: "#000",
              },
            }}
            onClick={() => setXtype("OPEN")}
          >
            <Search />
          </Box>
          <Box
            className={xtype === "FREE" ? "act" : ""}
            sx={{
              "&:last-child": { borderRight: "none" },
              borderRight: "1px solid #aaa",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#eee",
              color: "#555",
              "&:hover": {
                background: "#ddd",
                color: "#666",
              },
              "&.act": {
                background: "#fff",
                color: "#000",
              },
            }}
            onClick={() => setXtype("FREE")}
          >
            <ListAlt />
          </Box>
          <Box
            className={(xtype === "COLLISIONS" ? "act" : "") + ((collisionQuery?.data ?? []).length > 0 ? " collwarning" : "")}
            sx={{
              "&:last-child": { borderRight: "none" },
              borderRight: "1px solid #aaa",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#eee",
              color: "#555",
              "&:hover": {
                background: "#ddd",
                color: "#666",
              },
              "&.act": {
                background: "#fff",
                color: "#000",
              },
            }}
            onClick={() => setXtype("COLLISIONS")}
          >
            <Bolt />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            borderBottom: "1px solid #aaa",
            cursor: "pointer",
            display: "flex",
          }}
        >
          <Box
            className={xtype === "OPEN" ? "act" : ""}
            sx={{
              "&:last-child": { borderRight: "none" },
              borderRight: "1px solid #aaa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              background: "#eee",
              color: "#555",
              "&:hover": {
                background: "#ddd",
                color: "#666",
              },
              "&.act": {
                background: "#fff",
                color: "#000",
              },
            }}
            onClick={() => setXtype("OPEN")}
          >
            <Search />
          </Box>
          <Box
            className={xtype === "TRANSFERS" ? "act" : ""}
            sx={{
              "&:last-child": { borderRight: "none" },
              borderRight: "1px solid #aaa",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#eee",
              color: "#555",
              "&:hover": {
                background: "#ddd",
                color: "#666",
              },
              "&.act": {
                background: "#fff",
                color: "#000",
              },
            }}
            onClick={() => setXtype("TRANSFERS")}
          >
            <Redo />
          </Box>
        </Box>
      )}
      {xtype === "FREE" ? (
        <Box
          sx={{
            display: "flex",
            gap: "5px",
            alignItems: "center",
            borderBottom: "1px solid #aaa",
          }}
        >
          <IconButton size="small" onClick={() => setWeek(week.subtract(1, "week"))}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              minWidth: "145px",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              cursor: "pointer",
            }}
          >
            <Typography variant="h6">KW {week.format("W/GGGG")}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setWeek(week.add(1, "week"))}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
      ) : null}
      <Box
        sx={{
          overflow: "auto",
          position: "relative",
          background: "#fafafa",
          flex: 1,
        }}
      >
        {xtype === "COLLISIONS" ? (
          <CollisionContainer setCalendar={setCalendar} query={collisionQuery} />
        ) : xtype === "TRANSFERS" ? (
          <TransferContainer setCalendar={setCalendar} query={transferQuery} date={currentDate} />
        ) : xtype === "FREE" ? (
          <FreeContainer draggable={true} setCalendar={setCalendar} query={freeQuery} />
        ) : query.isLoading ? (
          <CircularProgress />
        ) : query.isLoadingError ? (
          "Error"
        ) : (query?.data ?? []).length === 0 ? (
          <Box sx={{ textAlign: "center", color: "#777", p: 1, fontSize: "14px" }}>Keine offenen Anfragen</Box>
        ) : (
          (query?.data ?? [])
            .sort((a, b) => (a.start ?? a.fromdate).localeCompare(b.start ?? b.fromdate))
            .map((x, i, f) => (
              <Fragment key={"roid" in x ? x.roid + "_" + x.open : x.id}>
                {i === 0 || (f[i - 1].fromdate ?? f[i - 1].start) !== (x.fromdate ?? x.start) ? (
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      color: "#555",
                      p: "4px 6px",
                      lineHeight: 1,
                      fontWeight: "14px",
                      mb: "8px",
                      flexShrink: 0,
                      background: "#edf0f9",
                      borderTop: "1px solid rgba(0,0,0,0.2)",
                      borderBottom: "1px solid rgba(0,0,0,0.2)",
                      boxShadow: "inset 0px -4px 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    ab {dayjs(x.fromdate ?? x.start).format("DD.MM.YYYY")}
                  </Box>
                ) : null}
                {"roid" in x ? (
                  <Box
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.5,
                        ...expandedEntryStyles,
                      },
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #777",
                      mb: "8px",
                      mx: "4px",
                      alignItems: "stretch",
                      overflow: "hidden",
                      borderRadius: "5px",
                      flexShrink: 0,
                      fontSize: "13px",
                    }}
                    onClick={() =>
                      setCalendar(
                        x.object_type === "KGP" ? "KGP" : x.TYPE === "CREW" ? "CREWS" : x.TYPE === "RESOURCE" ? "RESOURCES" : "STAFF",
                        x.resource_tag_id,
                        dayjs(x.fromdate, "YYYY-MM-DD"),
                      )
                    }
                  >
                    <Box sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                      <Box
                        sx={{
                          borderRight: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          lineHeight: 1,
                          background: "#edf0f9",
                        }}
                      >
                        {x.open}
                      </Box>
                      <Box
                        className="open-entry-text"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          p: "4px",
                          overflow: "hidden",
                          lineHeight: 1,
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {(x.TYPE === "CREW" ? "Kol. " : x.TYPE === "HUMAN" ? "Ma. " : "") + x.name}
                      </Box>
                      <Box
                        sx={{
                          borderLeft: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          fontSize: "12px",

                          lineHeight: "14px",

                          background: "#edf0f9",
                        }}
                      >
                        &rarr;{dayjs(x.todate).format("DD.MM.")}
                      </Box>
                    </Box>
                    <Box
                      className="open-entry-text"
                      sx={{
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        p: "2px",
                        backgroundColor: "#eee",
                      }}
                    >
                      <span style={{ fontSize: "12px", marginRight: "2px" }}>{x.pnum}</span>
                      {x.pname}
                    </Box>
                    {x.comment.length > 0 ? (
                      <Box
                        className="open-entry-text open-entry-comment"
                        sx={{
                          display: "-webkit-box",
                          p: "2px",
                          pt: "3px",
                          borderTop: "1px solid #aaa",
                          "-webkit-line-clamp": "2",
                          "-webkit-box-orient": "vertical",
                          overflow: "hidden",
                          fontSize: "12px",
                          lineHeight: 1.1,
                          textAlign: "right",
                          backgroundColor: "#f7f7f7",
                          boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {x.comment}
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        background: "#eee",
                        ...expandedEntryStyles,
                      },
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #f55",
                      mb: "8px",
                      mx: "4px",
                      alignItems: "stretch",
                      overflow: "hidden",
                      borderRadius: "5px",
                      flexShrink: 0,
                      fontSize: "13px",
                    }}
                    onClick={() =>
                      setCalendar(
                        x.object_type === "BTR_KGP" ? "KGP" : !x.staffMember ? "RESOURCES" : "STAFF",
                        x.resource_tag_id,
                        dayjs(x.start, "YYYY-MM-DD"),
                      )
                    }
                  >
                    <Box className="done" sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                      <Box
                        className="open-entry-text"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          p: "4px",
                          overflow: "hidden",
                          lineHeight: 1,
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {!x.staffMember ? <span style={{ fontSize: "12px", marginRight: "2px" }}>{x.resource.number}</span> : null}
                        {!x.staffMember ? x.resource.name : x.staffMember.name}
                      </Box>
                      <Box
                        className="done"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetRejectionMutation.mutate({ id: x.id });
                        }}
                        sx={{
                          borderLeft: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          "&:hover": {
                            background: "#8df98d",
                          },
                          lineHeight: "14px",

                          background: "#f9adad",
                        }}
                      >
                        <AssignmentTurnedIn fontSize="inherit" />
                      </Box>
                    </Box>
                    <Box
                      className="open-entry-text"
                      sx={{
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        p: "2px",
                        backgroundColor: "#eee",
                      }}
                    >
                      <span style={{ fontSize: "12px", marginRight: "2px" }}>{x.project.number}</span>
                      {x.project.name}
                    </Box>

                    <Box
                      className="open-entry-text open-entry-comment"
                      sx={{
                        display: "-webkit-box",
                        p: "2px",
                        pt: "3px",
                        borderTop: "1px solid #aaa",
                        "-webkit-line-clamp": "2",
                        "-webkit-box-orient": "vertical",
                        overflow: "hidden",
                        fontSize: "12px",
                        lineHeight: 1.1,
                        textAlign: "right",
                        backgroundColor: "#f7f7f7",
                        boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {x.type === "rejection" ? "Abgelehnt" : "Frei"}
                        {dayjs(x.end, "YYYY-MM-DD") < dayjs().add(2, "month") ? " bis " + dayjs(x.end, "YYYY-MM-DD").format("DD.MM.") : ""}
                      </span>
                      {x.comment} ({x.createdBy.firstName + " " + x.createdBy.name})
                    </Box>
                  </Box>
                )}
              </Fragment>
            ))
        )}
      </Box>
      <Box
        sx={{
          borderTop: "1px solid #aaa",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box
          onClick={() => setSideBarHidden(true)}
          sx={{
            borderLeft: "1px solid #aaa",
            "&:hover": { backgroundColor: "#eee" },
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: "5px",
          }}
        >
          <ArrowForwardIos />
        </Box>
      </Box>
      <div className={"thrash-overlay" + (showThrash ? " to-visible" : "")}>
        <Delete fontSize="inherit" />
      </div>
    </Box>
  );
}
